import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { dashboardsService, type DashboardInput } from '../../services/dashboards.service';
import type { Dashboard } from './types';

/**
 * Loads the user's visible boards (own + shared) and persists edits to the dashboards API.
 * Config/metadata edits update local state immediately and debounce-save the whole board.
 */
export function useDashboards() {
  const [boards, setBoards] = useState<Dashboard[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const boardsRef = useRef<Dashboard[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const sync = (next: Dashboard[]) => { boardsRef.current = next; setBoards(next); };

  const reload = useCallback(async () => {
    setLoading(true);
    try { sync(await dashboardsService.list()); } catch { /* leave empty */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const persist = useCallback((id: string) => {
    const b = boardsRef.current.find((x) => x.id === id);
    if (!b || !b.is_owner) return;
    setSaving(true);
    dashboardsService.update(id, { name: b.name, description: b.description ?? null, config: b.config, shared: b.shared })
      // Merge only the server's timestamp — never the echoed config/name/etc., which would
      // clobber edits the user made while this request was in flight (lost update).
      .then((updated) => sync(boardsRef.current.map((x) => (x.id === id ? { ...x, updated_at: updated.updated_at } : x))))
      .then(() => setSaveError(false))
      .catch(() => setSaveError(true))
      .finally(() => setSaving(false));
  }, []);

  const updateBoard = useCallback((id: string, patch: Partial<Dashboard>, opts?: { immediate?: boolean }) => {
    sync(boardsRef.current.map((b) => (b.id === id ? { ...b, ...patch } : b)));
    const t = timers.current.get(id);
    if (t) clearTimeout(t);
    if (opts?.immediate) persist(id);
    else timers.current.set(id, setTimeout(() => persist(id), 700));
  }, [persist]);

  const createBoard = useCallback(async (input: DashboardInput): Promise<Dashboard> => {
    const b = await dashboardsService.create(input);
    sync([b, ...boardsRef.current]);
    return b;
  }, []);

  const deleteBoard = useCallback(async (id: string): Promise<void> => {
    await dashboardsService.delete(id);
    sync(boardsRef.current.filter((b) => b.id !== id));
  }, []);

  // Flush a still-pending save on unmount so edit-then-navigate isn't lost.
  useEffect(() => () => {
    timers.current.forEach((t, id) => { clearTimeout(t); persist(id); });
  }, [persist]);

  return { boards, loading, saving, saveError, reload, createBoard, updateBoard, deleteBoard };
}
